import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { StorageService, Helpers } from '../utils/helpers';
import { LoadingOverlay } from '../components/ui/LoadingOverlay/LoadingOverlay';
import { mainReducer } from './store.reducer';
import * as Actions from './store.reducer';
import { IAppContext, IStoreState } from './store.actions';
import { DevSetupPage } from '../views/Error/DevSetupPage/DevSetupPage';
import { ApiNotRespondingPage } from '../views/Error/ApiNotRespondingPage/ApiNotRespondingPage';
import { IApiResponse, IAuthToken, IPulseResult } from '../utils/constants/interfaces';

const initialState: IStoreState = {
  user: {
    lastApiActivity: null,
    userId: null,
    userName: null,
    permissions: [],
    token: null,
    expiresAt: null,
    refreshAt: null
  },
  settings: {
    isLoading: false,
    devSetupRequired: false,
    apiReady: true,
    applicationReady: false,
    applicationVersion: ''
  }
};

const store = createContext<IAppContext>({
  state: initialState,
  dispatch: () => null
});

const useStoreContext = () => {
  const storeContext = useContext(store);
  const {
    state: { user, settings },
    dispatch
  } = storeContext;
  return {
    user,
    settings,
    dispatch,
    actions: Actions
  };
};

const { Provider } = store;

const AppProvider = ({ children }: { children: JSX.Element }) => {
  const [state, dispatch] = useReducer(mainReducer, initialState);

  //need to avoid using useApi hook, since useApi references store.tsx and creates a circular dependency
  const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';
  const apiGet = useCallback(
    async <T,>(endpoint: string): Promise<IApiResponse<T>> => {
      try {
        const apiResponse = await fetch(`${apiUrl}${endpoint}`);
        if (apiResponse.status === 200) {
          return (await apiResponse.json()) as IApiResponse<T>;
        } else {
          return Helpers.createApiResponse<T>(apiResponse.status, apiResponse.statusText);
        }
      } catch (e) {
        const errorText = Helpers.getErrorMessage(e);
        return Helpers.createApiResponse<T>(500, errorText);
      }
    },
    [apiUrl]
  );

  useEffect(() => {
    const init = async () => {
      //check if api is responsive
      const pulseResult = await apiGet<IPulseResult>(`/pulse`);
      if (!pulseResult || pulseResult.status?.statusCode !== 200) {
        dispatch(Actions.setApiReady(false));
      }

      //check if dev setup is required
      if (Helpers.isRunningLocal() && state.settings.apiReady) {
        const result = await apiGet<number>(`/dev/users/count`);
        const userCount: number = result?.response ?? 0;
        dispatch(Actions.setDevSetupRequired(userCount === 0));
      }

      //load user token from storage
      const storedToken = StorageService.getAuthToken();
      if (storedToken?.token) {
        const authToken: IAuthToken = Helpers.decodeAuthToken(storedToken?.token);
        dispatch(Actions.setUserAuthToken(authToken));
      }

      if (pulseResult.response?.version) {
        dispatch(Actions.setApplicationVersion(pulseResult.response.version));
      }
      dispatch(Actions.setApplicationReady(true));
    };
    void init();
  }, [apiGet, dispatch, state.settings.apiReady]);

  if (!state.settings.apiReady) {
    return <ApiNotRespondingPage />;
  }
  if (state.settings.devSetupRequired) {
    return <DevSetupPage />;
  }
  if (!state.settings.applicationReady) {
    return <LoadingOverlay />;
  }

  return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { store, useStoreContext, AppProvider };
